/**
 * Internal Governance Citations — admin mapping-review surface (DC-1669, v2.5 Pillar 5).
 *
 * The approval gate. An admin reviews AI-suggested clause → drawer-category
 * mappings and approves / edits / rejects them; nothing renders in a DQ/ML
 * drawer until a mapping reaches status='approved' here.
 *
 * Consumes the DC-1668 API (server/routes/governanceCitationRoutes.ts):
 *   GET   /api/governance-citations/mappings?status=
 *   PATCH /api/governance-citations/mappings/:id   { status?, category_id? }
 *
 * UX spec: docs/design/v2.5-governance-citations-ux-spec.md §2. Cream/teal
 * tokens only (lint:cream-guarded); H1–H10 reviewed at PR.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORY_DISPLAY_NAMES } from '../../types/dataQualityCategory';
import { METRIC_DISPLAY_NAMES } from '../../types/mlReadinessMetric';
import './citation-mappings.css';

type Surface = 'dq' | 'ml';
type Status = 'suggested' | 'approved' | 'rejected';

interface MappingRow {
  mapping_id: string;
  clause_id: string;
  document_id: string;
  document_title: string;
  section_ref: string | null;
  clause_text: string;
  surface: Surface;
  category_id: string;
  status: Status;
  confidence: number | null;
  suggested_by: 'ai' | 'admin';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

const DQ_OPTIONS = Object.entries(CATEGORY_DISPLAY_NAMES) as [string, string][];
const ML_OPTIONS = Object.entries(METRIC_DISPLAY_NAMES) as [string, string][];

function categoryLabel(surface: Surface, id: string): string {
  const map = surface === 'dq' ? CATEGORY_DISPLAY_NAMES : METRIC_DISPLAY_NAMES;
  return (map as Record<string, string>)[id] ?? id;
}

/** 3-band confidence (high ≥ .75, med ≥ .4, low otherwise). */
function confidenceBand(c: number | null): { level: 0 | 1 | 2 | 3; label: string } {
  if (c === null || !Number.isFinite(c)) return { level: 0, label: '—' };
  if (c >= 0.75) return { level: 3, label: 'High' };
  if (c >= 0.4) return { level: 2, label: 'Medium' };
  return { level: 1, label: 'Low' };
}

const STATUS_OPTIONS: { value: Status | 'all'; label: string }[] = [
  { value: 'suggested', label: 'Suggested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const CONFIDENCE_FLOORS: { value: number; label: string }[] = [
  { value: 0, label: 'Any' },
  { value: 0.4, label: '≥ Medium' },
  { value: 0.75, label: '≥ High' },
];

export function CitationMappings(): React.ReactElement {
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');

  // filters
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('suggested');
  const [surfaceFilter, setSurfaceFilter] = useState<Surface | 'all'>('all');
  const [documentFilter, setDocumentFilter] = useState<string>('all');
  const [minConfidence, setMinConfidence] = useState<number>(0);

  // interaction state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await fetch(`/api/governance-citations/mappings${qs}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      // pg returns NUMERIC (confidence) as a JSON string ("0.910"); coerce at
      // the boundary so the band meter, filter, and bulk logic see real numbers.
      const mapped: MappingRow[] = (Array.isArray(data.mappings) ? data.mappings : []).map(
        (m: MappingRow) => ({ ...m, confidence: m.confidence == null ? null : Number(m.confidence) }),
      );
      setRows(mapped);
      setSelected(new Set());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side filters layered on the status-scoped fetch.
  const visible = useMemo(() => {
    return rows.filter(r => {
      if (surfaceFilter !== 'all' && r.surface !== surfaceFilter) return false;
      if (documentFilter !== 'all' && r.document_id !== documentFilter) return false;
      if (minConfidence > 0 && (r.confidence ?? 0) < minConfidence) return false;
      return true;
    });
  }, [rows, surfaceFilter, documentFilter, minConfidence]);

  const documents = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) if (!seen.has(r.document_id)) seen.set(r.document_id, r.document_title);
    return Array.from(seen.entries());
  }, [rows]);

  // Group visible rows by document, preserving first-seen order.
  const groups = useMemo(() => {
    const map = new Map<string, { title: string; rows: MappingRow[] }>();
    for (const r of visible) {
      const g = map.get(r.document_id) ?? { title: r.document_title, rows: [] };
      g.rows.push(r);
      map.set(r.document_id, g);
    }
    return Array.from(map.values());
  }, [visible]);

  // Only suggested rows are bulk-approvable.
  const selectableIds = useMemo(
    () => visible.filter(r => r.status === 'suggested').map(r => r.mapping_id),
    [visible],
  );

  const patchMapping = useCallback(
    async (id: string, body: { status?: Status; category_id?: string }): Promise<MappingRow | null> => {
      setBusy(prev => new Set(prev).add(id));
      try {
        const res = await fetch(`/api/governance-citations/mappings/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Update failed: ${res.status}`);
        const data = await res.json();
        const m = data.mapping as (MappingRow & { id?: string }) | undefined;
        if (!m) return null;
        // The PATCH route returns the raw mapping row keyed by `id`; the list
        // route aliases it to `mapping_id`. Normalize so applyUpdated can match,
        // and coerce the NUMERIC confidence string to a number.
        return {
          ...m,
          mapping_id: m.mapping_id ?? (m.id as string),
          confidence: m.confidence == null ? null : Number(m.confidence),
        };
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Update failed');
        return null;
      } finally {
        setBusy(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [],
  );

  const applyUpdated = useCallback((updated: MappingRow) => {
    setRows(prev => prev.map(r => (r.mapping_id === updated.mapping_id ? { ...r, ...updated } : r)));
  }, []);

  const approve = useCallback(
    async (row: MappingRow) => {
      const updated = await patchMapping(row.mapping_id, { status: 'approved' });
      if (updated) {
        applyUpdated(updated);
        setAnnounce(`Approved — now cited under ${row.surface.toUpperCase()} · ${categoryLabel(row.surface, row.category_id)}.`);
      }
    },
    [patchMapping, applyUpdated],
  );

  const reject = useCallback(
    async (row: MappingRow) => {
      // H6: confirm only when un-publishing a live (approved) citation.
      if (row.status === 'approved') {
        const label = row.section_ref ? `${row.document_title} ${row.section_ref}` : row.document_title;
        if (!window.confirm(`Stop citing "${label}" under ${categoryLabel(row.surface, row.category_id)}?`)) return;
      }
      const updated = await patchMapping(row.mapping_id, { status: 'rejected' });
      if (updated) {
        applyUpdated(updated);
        setAnnounce('Mapping rejected.');
      }
    },
    [patchMapping, applyUpdated],
  );

  const openEdit = useCallback((row: MappingRow) => {
    setEditing(row.mapping_id);
    setEditCategory(row.category_id);
  }, []);

  const saveEdit = useCallback(
    async (row: MappingRow) => {
      if (!editCategory || editCategory === row.category_id) {
        // category unchanged → just approve in place
        const updated = await patchMapping(row.mapping_id, { status: 'approved' });
        if (updated) applyUpdated(updated);
      } else {
        const updated = await patchMapping(row.mapping_id, { category_id: editCategory, status: 'approved' });
        if (updated) {
          applyUpdated(updated);
          setAnnounce(`Re-targeted to ${row.surface.toUpperCase()} · ${categoryLabel(row.surface, editCategory)} and approved.`);
        }
      }
      setEditing(null);
    },
    [editCategory, patchMapping, applyUpdated],
  );

  const bulkApprove = useCallback(async () => {
    const ids = Array.from(selected).filter(id => selectableIds.includes(id));
    if (ids.length === 0) return;
    const band = CONFIDENCE_FLOORS.find(f => f.value === minConfidence)?.label ?? 'Any';
    const bandSuffix = minConfidence > 0 ? ` with confidence ${band}` : '';
    if (!window.confirm(`Approve ${ids.length} mapping${ids.length === 1 ? '' : 's'}${bandSuffix}?`)) return;
    for (const id of ids) {
      const row = rows.find(r => r.mapping_id === id);
      if (!row) continue;
      const updated = await patchMapping(id, { status: 'approved' });
      if (updated) applyUpdated(updated);
    }
    setSelected(new Set());
    setAnnounce(`Approved ${ids.length} mapping${ids.length === 1 ? '' : 's'}.`);
  }, [selected, selectableIds, minConfidence, rows, patchMapping, applyUpdated]);

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected(prev => (prev.size === selectableIds.length ? new Set() : new Set(selectableIds)));
  }, [selectableIds]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedApprovable = Array.from(selected).filter(id => selectableIds.includes(id)).length;

  return (
    <div className="cm-view">
      <header className="cm-head">
        <p className="cm-eyebrow">Governance · Citation Mappings</p>
        <h1 className="cm-headline">Review internal governance citations</h1>
        <p className="cm-sub">
          Review AI-suggested links between your governance clauses and assessment categories.
          Nothing appears in a Data Quality or ML Readiness drawer until you approve it here.
        </p>
        <Link className="cm-backlink" to="/settings/governance/documents">← Governance documents</Link>
      </header>

      <div className="cm-filterbar" role="group" aria-label="Filters">
        <label className="cm-field">
          <span>Status</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as Status | 'all')}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="cm-field">
          <span>Document</span>
          <select value={documentFilter} onChange={e => setDocumentFilter(e.target.value)}>
            <option value="all">All</option>
            {documents.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
          </select>
        </label>
        <label className="cm-field">
          <span>Surface</span>
          <select value={surfaceFilter} onChange={e => setSurfaceFilter(e.target.value as Surface | 'all')}>
            <option value="all">All</option>
            <option value="dq">Data Quality</option>
            <option value="ml">ML Readiness</option>
          </select>
        </label>
        <label className="cm-field">
          <span>Confidence</span>
          <select value={minConfidence} onChange={e => setMinConfidence(Number(e.target.value))}>
            {CONFIDENCE_FLOORS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="cm-btn cm-btn--primary cm-bulk"
          onClick={bulkApprove}
          disabled={loading || selectedApprovable === 0}
        >
          Bulk-approve ({selectedApprovable})
        </button>
      </div>

      <div className="cm-live" aria-live="polite">{announce}</div>

      {error && (
        <div className="cm-alert" role="alert">
          Couldn&apos;t load mappings. {error}
          <button type="button" className="cm-btn cm-btn--ghost" onClick={load}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="cm-skeleton" aria-hidden="true">
          {[0, 1, 2, 3].map(i => <div key={i} className="cm-skel-row" />)}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState statusFilter={statusFilter} hasAny={rows.length > 0} />
      ) : (
        <table className="cm-table">
          <thead>
            <tr>
              <th scope="col" className="cm-col-sel">
                <input
                  type="checkbox"
                  aria-label="Select all suggested mappings"
                  checked={selectableIds.length > 0 && selected.size === selectableIds.length}
                  onChange={toggleSelectAll}
                  disabled={selectableIds.length === 0}
                />
              </th>
              <th scope="col">Clause</th>
              <th scope="col">Maps to</th>
              <th scope="col">Confidence</th>
              <th scope="col">Status</th>
              <th scope="col" className="cm-col-actions">Actions</th>
            </tr>
          </thead>
          {groups.map(group => (
            <tbody key={group.title}>
              <tr className="cm-grouprow">
                <th scope="rowgroup" colSpan={6}>
                  {group.title}
                  <span className="cm-groupcount">{group.rows.length} mapping{group.rows.length === 1 ? '' : 's'}</span>
                </th>
              </tr>
              {group.rows.map(row => {
                const band = confidenceBand(row.confidence);
                const isBusy = busy.has(row.mapping_id);
                const isOpen = expanded.has(row.mapping_id);
                const options = row.surface === 'dq' ? DQ_OPTIONS : ML_OPTIONS;
                return (
                  <tr key={row.mapping_id} className={isBusy ? 'cm-row cm-row--busy' : 'cm-row'}>
                    <td className="cm-col-sel">
                      <input
                        type="checkbox"
                        aria-label={`Select mapping for ${categoryLabel(row.surface, row.category_id)}`}
                        checked={selected.has(row.mapping_id)}
                        onChange={() => toggleSelect(row.mapping_id)}
                        disabled={row.status !== 'suggested'}
                      />
                    </td>
                    <td className="cm-col-clause">
                      <blockquote className={isOpen ? 'cm-clause' : 'cm-clause cm-clause--clamp'}>
                        {row.clause_text}
                      </blockquote>
                      <div className="cm-clause-meta">
                        {row.section_ref && <span className="cm-section">{row.section_ref}</span>}
                        {row.clause_text.length > 140 && (
                          <button type="button" className="cm-linkbtn" onClick={() => toggleExpand(row.mapping_id)}>
                            {isOpen ? 'Show less' : 'Show full clause'}
                          </button>
                        )}
                        <span className="cm-byline">
                          {row.suggested_by === 'ai' ? 'Suggested by Bridge AI' : 'Added by admin'}
                        </span>
                      </div>
                    </td>
                    <td className="cm-col-maps">
                      {editing === row.mapping_id ? (
                        <div className="cm-edit">
                          <span className={`cm-chip cm-chip--${row.surface}`}>{row.surface.toUpperCase()}</span>
                          <label className="cm-visually-hidden" htmlFor={`cat-${row.mapping_id}`}>Category</label>
                          <select
                            id={`cat-${row.mapping_id}`}
                            value={editCategory}
                            onChange={e => setEditCategory(e.target.value)}
                          >
                            {options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                          </select>
                          <button type="button" className="cm-btn cm-btn--primary" onClick={() => saveEdit(row)} disabled={isBusy}>Save &amp; approve</button>
                          <button type="button" className="cm-btn cm-btn--ghost" onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <span className={`cm-chip cm-chip--${row.surface}`}>{row.surface.toUpperCase()}</span>
                          <span className="cm-category">{categoryLabel(row.surface, row.category_id)}</span>
                        </>
                      )}
                    </td>
                    <td className="cm-col-conf">
                      <span className={`cm-meter cm-meter--${band.level}`} aria-hidden="true">
                        <i /><i /><i />
                      </span>
                      <span className="cm-conf-label">{band.label}</span>
                    </td>
                    <td className="cm-col-status">
                      <span className={`cm-status cm-status--${row.status}`}>
                        {row.status === 'suggested' ? 'Suggested' : row.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </td>
                    <td className="cm-col-actions">
                      <div className="cm-actions">
                        {row.status !== 'approved' && (
                          <button type="button" className="cm-iconbtn cm-iconbtn--approve" aria-label="Approve mapping" title="Approve" disabled={isBusy} onClick={() => approve(row)}>✓</button>
                        )}
                        <button type="button" className="cm-iconbtn cm-iconbtn--edit" aria-label="Edit category" title="Edit category" disabled={isBusy} onClick={() => openEdit(row)}>✎</button>
                        {row.status !== 'rejected' && (
                          <button type="button" className="cm-iconbtn cm-iconbtn--reject" aria-label="Reject mapping" title="Reject" disabled={isBusy} onClick={() => reject(row)}>✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      )}
    </div>
  );
}

function EmptyState({ statusFilter, hasAny }: { statusFilter: Status | 'all'; hasAny: boolean }): React.ReactElement {
  if (hasAny) {
    return (
      <div className="cm-empty">
        <p>No mappings match the current filters.</p>
      </div>
    );
  }
  if (statusFilter === 'suggested') {
    return (
      <div className="cm-empty">
        <p>No suggestions to review yet.</p>
        <p className="cm-empty-sub">
          Open a governance document and turn on <strong>&ldquo;Use this document as an internal citation source&rdquo;</strong>{' '}
          to start. Compass extracts clauses and proposes mappings after the document is analyzed.
        </p>
        <Link className="cm-btn cm-btn--primary" to="/settings/governance/documents">Go to governance documents</Link>
      </div>
    );
  }
  return (
    <div className="cm-empty">
      <p>Nothing here for this status.</p>
    </div>
  );
}

export default CitationMappings;
